import React, { FC, useEffect, useState, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns, extendProductColumns, ExportDataType, REQUIRED_EXPEDITION_PACKAGES_GRID_COLUMNS } from './constants';
import Table from 'src/components/Table';
import {
    TablePropsOptionsType,
    OnChangeOptionsType,
    ExportTypeType,
    TableRefType,
    TableColumnType,
} from 'src/types/table';
import { OrderIcon } from 'src/components/SkladonIcon/Icons';
import { useDebounce } from 'src/utils/debounce';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import { OrderExpeditionPackageType } from 'src/types/orderExpeditionPackage';
import { useCheckPermission } from 'src/utils/checkPermission';
import GradientIcon from '@mui/icons-material/Gradient';
import { getOrderExpeditionPackagesGridFetch } from 'src/resources/orderExpeditionPackagesGrid/reducer';
import {
    ORDER_EXPEDITION_PACKAGE_GRID_URL,
    ORDER_EXPEDITION_PACKAGE_PRODUCTS_URL,
} from 'src/resources/orderExpeditionPackagesGrid/sagas';

export const SESSION_NAME = 'orderExpeditionPackagesTable';

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'CREATED_AT', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: [],
};

const OrderExpeditionPackagesContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [formatedData, setFormatedData] = useState<OrderExpeditionPackageType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);

    const loading = useSelector((state: RootStoreType) => state.orderExpeditionPackagesGrid.loading);
    const orderExpeditionPackagesData = useSelector((state: RootStoreType) => state.orderExpeditionPackagesGrid.data);

    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);

    const permissionOrderExpeditionPackageDetail = useCheckPermission('ORDER.EXPEDITION_PACKAGE.DETAIL');
    const permissionOrderDetail = useCheckPermission('ORDER.DETAIL');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('orderExpeditionPackages.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        loadTableData();
    }, [debouncedTableOptions]);

    useEffect(() => {
        if (orderExpeditionPackagesData?.data) {
            setFormatedData(getFormatedData(orderExpeditionPackagesData?.data || []));
        }
    }, [orderExpeditionPackagesData?.data]);

    const getFormatedData = (data: OrderExpeditionPackageType[]): OrderExpeditionPackageType[] => {
        return data;
    };

    const getAllGridParams = () => ({
        filters: [[...(tableOptions.filters || [])]],
        sorts: [tableOptions.sort],
        page: tableOptions.page,
        items: tableOptions.items,
        columns: [
            ...REQUIRED_EXPEDITION_PACKAGES_GRID_COLUMNS,
            ...tableOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key),
        ].filter(filterUnique),
    });

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getOrderExpeditionPackagesGridFetch({ params: getAllGridParams() }));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const handleExport = (type: ExportTypeType, dataType: ExportDataType) => {
        const isProducts = dataType === 'packagesProducts';
        const fileName = isProducts
            ? 'orderExpeditionPackages.action.exportWithProductsFileName'
            : 'orderExpeditionPackages.action.exportFileName';
        const url = isProducts ? ORDER_EXPEDITION_PACKAGE_PRODUCTS_URL : ORDER_EXPEDITION_PACKAGE_GRID_URL;
        const extendColumns: TableColumnType[] | undefined = isProducts ? extendProductColumns : undefined;

        tableRef.current?.exportTableGrid(type, translate(fileName), getAllGridParams, url, extendColumns);
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const reloadTableData = () => {
        dispatch(getOrderExpeditionPackagesGridFetch(undefined));
    };

    return (
        <>
            <TopBlock handleExport={handleExport} reloadTableData={reloadTableData} />
            <Table
                allColumns={allColumns}
                ref={tableRef}
                rowIdKey="ID"
                rows={formatedData}
                loading={loading}
                columsLabelPrefix="orderExpeditionPackages.table"
                options={tableOptions}
                totalCount={orderExpeditionPackagesData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={[
                    {
                        icon: <GradientIcon />,
                        label: translate('orderExpeditionPackages.action.orderExpeditionPackageDetail'),
                        getLink: (row) => `/orders/expedition_packages/${row.ID}/`,
                        hiddenFce: () => !permissionOrderExpeditionPackageDetail,
                    },
                    {
                        icon: <OrderIcon />,
                        label: translate('orderExpeditionPackages.action.orderDetail'),
                        getLink: (row) => `/orders/${row.ID_ORDER}/`,
                        hiddenFce: (row) => !row.ID_ORDER || !permissionOrderDetail,
                    },
                ]}
                userSettingsName="showOrderExpeditionPackagesGridColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                optionsSessionName={SESSION_NAME}
                heightVariant="top-tab"
                fullHeight
            />
        </>
    );
};

export default OrderExpeditionPackagesContainer;
